import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '@/config';
import { getTransactions } from '@/domains/hrum/api/economyApi';
import { useHrumToast } from './HrumToast';
import { getAudioFileExtensionFromMimeType, pickSupportedAudioRecordingMimeType } from '@/utils/audioFormats';

function getBlobDurationMs(blob) {
  return new Promise((resolve) => {
    if (!blob || typeof window === 'undefined') {
      resolve(null);
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const audio = document.createElement('audio');

    const cleanup = () => {
      audio.src = '';
      URL.revokeObjectURL(objectUrl);
    };

    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const duration = Number(audio.duration);
      cleanup();
      resolve(Number.isFinite(duration) && duration > 0 ? Math.round(duration * 1000) : null);
    };
    audio.onerror = () => {
      cleanup();
      resolve(null);
    };
    audio.src = objectUrl;
  });
}

function MessageInput({ chat, chatId, socket, token, onTyping }) {
  const { showEarn } = useHrumToast();
  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastEconomyProbeAtRef = useRef(0);
  const maxLength = chat?.isAiChat ? 2000 : 10000;

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  // Индикатор «печатает»
  const handleInputChange = (e) => {
    setInput(String(e.target.value || '').slice(0, maxLength));

    if (socket && chatId) {
      socket.emit('typing:start', { chatId });

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('typing:stop', { chatId });
      }, 2000);
    }
  };

  const handleSend = (e) => {
    e?.preventDefault();
    if (!input.trim() || !chatId || !socket) return;

    const text = input.trim();

    socket.emit('message:send', {
      chatId,
      text,
      type: 'text'
    }, (response) => {
      if (!response?.success) return;
      if (!token) return;
      if (String(text).trim().length < 20) return;

      const now = Date.now();
      if (now - lastEconomyProbeAtRef.current < 1200) return;
      lastEconomyProbeAtRef.current = now;

      setTimeout(async () => {
        try {
          const data = await getTransactions({ token, limit: 1 });
          const t = data?.items?.[0];
          if (!t || t.reasonCode !== 'earn:message') return;
          const raw = String(t.deltaHrum ?? '').trim();
          const abs = raw.startsWith('-') ? raw.slice(1) : raw.startsWith('+') ? raw.slice(1) : raw;
          if (!abs) return;
          showEarn({ amountHrum: abs, txId: t.id });
        } catch (_) { }
      }, 650);
    });

    setInput('');
    socket.emit('typing:stop', { chatId });
  };

  // === ФАЙЛЫ ===
  const handleFileSelect = () => fileInputRef.current?.click();

  const uploadFile = async (file) => {
    if (!file || !chatId) return;

    // Лимит 100 МБ
    if (file.size > 100 * 1024 * 1024) {
      alert('Максимальный размер файла: 100 МБ');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const type = (() => {
        if (file.type.startsWith('image/')) return 'image';
        if (file.type.startsWith('video/')) return 'video';
        // Fallback по расширению если браузер не определил MIME-тип
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
        const videoExts = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'];
        if (imageExts.includes(ext)) return 'image';
        if (videoExts.includes(ext)) return 'video';
        return 'file';
      })();

      socket.emit('message:send', {
        chatId,
        type,
        text: '',
        attachment: {
          url: res.data.url,
          originalName: res.data.originalName,
          mimeType: res.data.mimeType,
          size: res.data.size
        }
      });
    } catch (err) {
      console.error('Upload error:', err);
      alert('Ошибка загрузки файла');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  // Drag & Drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  // === ГОЛОСОВЫЕ СООБЩЕНИЯ ===
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Предпочитаем формат, который браузер умеет и записывать, и воспроизводить.
      const mimeType = pickSupportedAudioRecordingMimeType();

      console.log('[MessageInput] Recording with mimeType:', mimeType);

      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('[MessageInput] Recorder error:', event?.error || event);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());

        const actualMimeType = mediaRecorder.mimeType || mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        const durationMs = await getBlobDurationMs(audioBlob);

        console.log('[MessageInput] Recording stopped, blob size:', audioBlob.size, 'type:', actualMimeType);

        if (audioBlob.size > 0) {
          await uploadVoiceMessage(audioBlob, actualMimeType, durationMs);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } catch (err) {
      console.error('Microphone access denied:', err);
      alert('Нет доступа к микрофону');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      audioChunksRef.current = [];
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const uploadVoiceMessage = async (blob, mimeType, durationMs) => {
    setUploading(true);
    try {
      const extension = getAudioFileExtensionFromMimeType(mimeType);

      const formData = new FormData();
      formData.append('file', blob, `voice_${Date.now()}${extension}`);

      const res = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('[MessageInput] Voice uploaded:', res.data);
      const voiceAttachment = {
        url: res.data.url,
        mimeType: mimeType || 'audio/webm',
        size: res.data.size
      };
      voiceAttachment.originalName = 'Голосовое сообщение';
      voiceAttachment.originalName = '\u0413\u043e\u043b\u043e\u0441\u043e\u0432\u043e\u0435 \u0441\u043e\u043e\u0431\u0449\u0435\u043d\u0438\u0435';
      if (durationMs && durationMs > 0) {
        voiceAttachment.durationMs = durationMs;
      }

      socket.emit('message:send', {
        chatId,
        type: 'voice',
        text: '',
        attachment: voiceAttachment, /*
          originalName: 'Голосовое сообщение',
        */
      });
    } catch (err) {
      console.error('Voice upload error:', err);
      alert('Ошибка отправки голосового');
    } finally {
      setUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const canSend = input.trim().length > 0 && !uploading;

  return (
    <div
      className={`message-input-container ${isDragging ? 'dragging' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="drop-overlay">
          <span className="drop-text">📎 Отпустите для загрузки</span>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
      />

      {isRecording ? (
        // Режим записи
        <div className="recording-row">
          <div className="recording-indicator">
            <span className="recording-dot" />
            <span className="recording-time">{formatTime(recordingTime)}</span>
          </div>
          <button onClick={cancelRecording} className="icon-btn-circle cancel" title="Отмена">
            ✕
          </button>
          <button onClick={stopRecording} className="icon-btn-circle send-voice" title="Отправить">
            ➤
          </button>
        </div>
      ) : (
        // Обычный режим
        <form onSubmit={handleSend} className="input-form">
          <button
            type="button"
            onClick={handleFileSelect}
            className="icon-btn-circle attachment"
            disabled={uploading}
            title="Прикрепить файл"
          >
            📎
          </button>

          <input
            type="text"
            value={input}
            onChange={handleInputChange}
            placeholder={uploading ? 'Загрузка...' : 'Введите сообщение...'}
            className="message-input-field"
            maxLength={maxLength}
            disabled={uploading}
            enterKeyHint="send"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />

          {input.trim() ? (
            <button
              type="submit"
              className={`icon-btn-circle send ${!canSend ? 'disabled' : ''}`}
              disabled={!canSend}
            >
              ➤
            </button>
          ) : (
            <button
              type="button"
              onClick={startRecording}
              className="icon-btn-circle mic"
              disabled={uploading}
              title="Голосовое сообщение"
            >
              🎤
            </button>
          )}
        </form>
      )}

      <style>{`
        .message-input-container {
            padding: 12px 16px max(12px, var(--safe-area-bottom));
            border-top: 1px solid var(--border-color);
            background-color: var(--bg-surface);
            position: relative;
            flex-shrink: 0;
        }

        .message-input-container.dragging {
            background-color: rgba(59, 130, 246, 0.1);
            border: 2px dashed var(--accent);
        }

        .drop-overlay {
            position: absolute; top: 0; left: 0; right: 0; bottom: 0;
            display: flex; align-items: center; justify-content: center;
            background: rgba(30, 64, 175, 0.9); border-radius: 8px; z-index: 10;
        }
        .drop-text { color: white; font-size: 16px; font-weight: 600; }

        .input-form { display: flex; align-items: center; gap: 8px; min-width: 0; }

        .message-input-field {
            flex: 1; min-width: 0; height: 44px; padding: 0 16px; background-color: var(--bg-input);
            border: 1px solid var(--border-input); border-radius: 24px;
            color: var(--text-primary); font-size: 14px; outline: none; transition: border-color 0.2s;
            line-height: 44px;
        }
        .message-input-field:focus { border-color: var(--accent); }

        .icon-btn-circle {
            width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;
            background: transparent; border: none; border-radius: 50%; font-size: 20px;
            cursor: pointer; transition: background 0.2s; color: var(--text-secondary); flex-shrink: 0;
        }
        .icon-btn-circle:hover { background-color: var(--bg-hover); color: var(--text-primary); }

        .icon-btn-circle.send { background-color: var(--accent); color: white; font-size: 16px; }
        .icon-btn-circle.send:hover { background-color: var(--accent-hover); }
        .icon-btn-circle.send.disabled { background-color: var(--bg-disabled); cursor: not-allowed; opacity: 0.7; }

        .icon-btn-circle.mic { background-color: var(--bg-surface); color: var(--text-secondary); border: 1px solid var(--border-color); }
        .icon-btn-circle.mic:hover { background-color: var(--bg-hover); color: var(--accent); border-color: var(--accent); }
        
        .icon-btn-circle.attachment:hover { color: var(--accent); }

        /* Recording */
        .recording-row { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .recording-indicator {
            flex: 1; min-width: 0; display: flex; align-items: center; gap: 10px; padding: 12px 16px;
            background-color: var(--bg-input); border-radius: 24px;
        }
        .recording-dot {
            width: 10px; height: 10px; background-color: var(--danger); border-radius: 50%;
            animation: pulse 1s infinite;
        }
        .recording-time { color: var(--text-primary); font-size: 14px; font-weight: 500; }

        .icon-btn-circle.cancel { background-color: rgba(239, 68, 68, 0.1); color: var(--danger); border: 1px solid var(--danger); }
        .icon-btn-circle.cancel:hover { background-color: var(--danger); color: white; }

        .icon-btn-circle.send-voice { background-color: var(--success); color: white; border: none; }
        .icon-btn-circle.send-voice:hover { opacity: 0.9; }

        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }

        @media (max-width: 768px) {
            .message-input-container {
                padding: 8px 10px max(8px, var(--safe-area-bottom));
                background: rgba(15, 23, 40, 0.98);
            }

            .input-form,
            .recording-row {
                gap: 6px;
            }

            .message-input-field {
                height: 42px;
                padding: 0 14px;
                font-size: 16px;
                line-height: 42px;
                border-radius: 21px;
            }

            .icon-btn-circle {
                width: 42px;
                height: 42px;
            }
        }

        @media (max-width: 420px) {
            .message-input-container {
                padding-left: 8px;
                padding-right: 8px;
            }

            .input-form,
            .recording-row {
                gap: 5px;
            }

            .icon-btn-circle {
                width: 38px;
                height: 38px;
                font-size: 18px;
            }

            .message-input-field {
                height: 40px;
                padding: 0 12px;
                line-height: 40px;
            }
        }
      `}</style>
    </div>
  );
}

export default MessageInput;
